import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import { Wand2, Zap, FileText, Sparkles, Loader2 } from 'lucide-react';
import { GlassButton } from '@/components/ui/glass-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { AICustomPromptDialog } from './AICustomPromptDialog';

interface AIToolbarProps {
  editor: Editor;
  className?: string;
}

export function AIToolbar({ editor, className }: AIToolbarProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  const handleAIAction = async (operation: string, actionName: string, customPromptText?: string) => {
    setIsLoading(true);
    
    try {
      let text = '';
      
      if (operation === 'continue') {
        text = editor.getText();
      } else {
        const selection = editor.state.selection;
        text = editor.state.doc.textBetween(selection.from, selection.to);
      }

      if (!text.trim()) {
        toast({
          title: operation === 'continue' ? 'No content' : 'No text selected',
          description: operation === 'continue' 
            ? 'Please write something first' 
            : `Please select some text to ${actionName.toLowerCase()}`,
          variant: 'destructive',
        });
        setIsLoading(false);
        return;
      }

      toast({
        title: 'AI Processing',
        description: `${actionName} in progress...`,
      });

      const { data, error } = await supabase.functions.invoke('ai-text-tools', {
        body: { 
          operation, 
          text,
          customPrompt: customPromptText 
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.result) {
        throw new Error('No result from AI');
      }

      if (operation === 'continue') {
        editor.chain().focus().insertContent(data.result).run();
      } else {
        editor.chain().focus().deleteSelection().insertContent(data.result).run();
      }

      toast({
        title: 'Success',
        description: `${actionName} completed!`,
      });

    } catch (error) {
      console.error('AI processing error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Something went wrong';
      toast({
        title: 'AI Error',
        description: errorMessage.includes('Rate limit') 
          ? 'Too many requests. Please wait a moment.' 
          : 'AI processing failed. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const improveSelection = () => handleAIAction('improve', 'Improving text');
  const expandSelection = () => handleAIAction('expand', 'Expanding text');
  const summarizeSelection = () => handleAIAction('summarize', 'Summarizing text');
  const continueWriting = () => handleAIAction('continue', 'Continuing writing');

  return (
    <>
      <div className={cn("flex items-center gap-1", className)}>
        {/* Improve Button */}
        <Popover>
          <PopoverTrigger asChild>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={improveSelection}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
            </GlassButton>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            className="w-auto px-3 py-1.5 text-xs bg-popover/95 backdrop-blur-sm border-border/50 z-[9999]" 
            sideOffset={5}
          >
            Improve text
          </PopoverContent>
        </Popover>

        {/* Expand Button */}
        <Popover>
          <PopoverTrigger asChild>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={expandSelection}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              <Zap className="h-4 w-4" />
            </GlassButton>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            className="w-auto px-3 py-1.5 text-xs bg-popover/95 backdrop-blur-sm border-border/50 z-[9999]" 
            sideOffset={5}
          >
            Expand text
          </PopoverContent>
        </Popover>

        {/* Summarize Button */}
        <Popover>
          <PopoverTrigger asChild>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={summarizeSelection}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              <FileText className="h-4 w-4" />
            </GlassButton>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            className="w-auto px-3 py-1.5 text-xs bg-popover/95 backdrop-blur-sm border-border/50 z-[9999]" 
            sideOffset={5}
          >
            Summarize text
          </PopoverContent>
        </Popover>

        {/* Separator */}
        <div className="w-px h-6 bg-border/40 mx-1" />

        {/* Continue Writing Button */}
        <Popover>
          <PopoverTrigger asChild>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={continueWriting}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isLoading}
              className="h-8 px-3 gap-2"
            >
              <Sparkles className="h-4 w-4" />
              <span className="text-xs">Continue</span>
            </GlassButton>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            className="w-auto px-3 py-1.5 text-xs bg-popover/95 backdrop-blur-sm border-border/50 z-[9999]" 
            sideOffset={5}
          >
            Continue writing
          </PopoverContent>
        </Popover>

        {/* Custom Prompt Button */}
        <Popover>
          <PopoverTrigger asChild>
            <GlassButton
              variant="ghost"
              size="sm"
              onClick={() => setDialogOpen(true)}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isLoading}
              className="h-8 w-8 p-0"
            >
              <Sparkles className="h-4 w-4" />
            </GlassButton>
          </PopoverTrigger>
          <PopoverContent 
            side="top" 
            className="w-auto px-3 py-1.5 text-xs bg-popover/95 backdrop-blur-sm border-border/50 z-[9999]" 
            sideOffset={5}
          >
            Custom prompt
          </PopoverContent>
        </Popover>
      </div>

      <AICustomPromptDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editor={editor}
      />
    </>
  );
}
